import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Chart from "react-apexcharts";
import { Alert, Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, Row, Table } from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import client, { getApiError } from "../../api/client";
import { useAuth } from "../../Auth/AuthContext";
import { EmptyState, ErrorState, LoadingState } from "../Common/WorkspaceState";

const MODULES = {
  dashboard: { title: "Dashboard", description: "A current view of your company workspace and lead pipeline." },
  planning: { title: "Planning", description: "Plan campaigns, content, and follow-up work.", countKey: "planning" },
  analytics: { title: "Analytics", description: "Monitor lead pipeline and channel performance." },
  reviews: { title: "Reviews", description: "Monitor and respond to customer reviews.", countKey: "reviews" },
  reporting: { title: "Reporting", description: "Generate and export company reports.", countKey: "reports" },
  inbox: { title: "Inbox", description: "Handle messages and lead emails in one place.", countKey: "inbox_unread" },
  "business-impact": { title: "Business Impact", description: "Connect presence activity to pipeline outcomes." },
  "online-presence": { title: "Online Presence", description: "Manage connected social and business profiles.", countKey: "integrations" },
  guidance: { title: "Guidance & Updates", description: "Review actionable recommendations and platform changes." },
  settings: { title: "Settings", description: "Manage your company workspace, team, roles, and integrations." },
};

const StatCard = ({ label, value }) => (
  <Col sm="6" xl="3">
    <Card><CardBody><span className="text-muted">{label}</span><h3 className="mb-0 mt-2">{value}</h3></CardBody></Card>
  </Col>
);

const WorkspaceContext = () => {
  const { user, dateRange } = useAuth();
  return <Alert color="light" className="d-flex flex-wrap justify-content-between align-items-center gap-2" role="status">
    <span><strong>Company:</strong> {user?.company?.name || "Not selected"}</span>
    <span><strong>Date range:</strong> {dateRange.start} to {dateRange.end}</span>
  </Alert>;
};

const Leads = () => {
  const [result, setResult] = useState({ data: [], current_page: 1, last_page: 1, total: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", source: "", status: "new", priority: "normal", estimated_value: "", notes: "" });
  const { user, hasPermission } = useAuth();
  const can = hasPermission;

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [leadsResponse, dashboardResponse] = await Promise.all([
        client.get("/leads", { params: { page, search: search || undefined, status: status || undefined } }),
        client.get("/dashboard"),
      ]);
      setResult(leadsResponse.data);
      setSummary(dashboardResponse.data.leads);
    } catch (requestError) { setError(getApiError(requestError, "Unable to load leads.")); }
    finally { setLoading(false); }
  }, [page, search, status, user?.company?.id]);

  useEffect(() => { const timer = setTimeout(load, 250); return () => clearTimeout(timer); }, [load]);

  const submit = async (event) => {
    event.preventDefault(); setSaving(true); setError("");
    try {
      const payload = { ...form, estimated_value: form.estimated_value || null };
      if (editing) await client.put(`/leads/${editing}`, payload);
      else await client.post("/leads", payload);
      setForm({ first_name: "", last_name: "", email: "", phone: "", source: "", status: "new", priority: "normal", estimated_value: "", notes: "" });
      setEditing(null); setShowForm(false); setPage(1); await load();
    } catch (requestError) { setError(getApiError(requestError, `Unable to ${editing ? "update" : "create"} lead.`)); }
    finally { setSaving(false); }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete ${lead.first_name} ${lead.last_name || ""}?`)) return;
    try { await client.delete(`/leads/${lead.id}`); await load(); }
    catch (requestError) { setError(getApiError(requestError, "Unable to delete lead.")); }
  };

  const edit = (lead) => {
    setEditing(lead.id);
    setForm({
      first_name: lead.first_name || "", last_name: lead.last_name || "", email: lead.email || "", phone: lead.phone || "",
      source: lead.source || "", status: lead.status || "new", priority: lead.priority || "normal",
      estimated_value: lead.estimated_value || "", notes: lead.notes || "",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => {
    setEditing(null); setShowForm(false);
    setForm({ first_name: "", last_name: "", email: "", phone: "", source: "", status: "new", priority: "normal", estimated_value: "", notes: "" });
  };

  return <>
    <Breadcrumbs mainTitle="Leads" parent="Workspace" title="Leads" />
    <Container fluid>
      <WorkspaceContext />
      {error && <Alert color="danger">{error}</Alert>}
      {summary && <Row><StatCard label="All leads" value={summary.total} /><StatCard label="New" value={summary.new} /><StatCard label="Qualified" value={summary.qualified} /><StatCard label="Won" value={summary.won} /></Row>}
      <Card>
        <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <div><h5 className="mb-1">Lead pipeline</h5><span className="text-muted">{result.total} total</span></div>
          {(showForm || can("leads.create")) && <Button color="primary" onClick={() => showForm ? closeForm() : setShowForm(true)}>{showForm ? "Cancel" : "Add lead"}</Button>}
        </CardHeader>
        <CardBody>
          {showForm && <Form onSubmit={submit} className="mb-4 border-bottom pb-4"><Row>
            <Col md="4"><FormGroup><Label for="lead-first-name">First name</Label><Input id="lead-first-name" required maxLength="120" value={form.first_name} onChange={(e) => setForm({...form, first_name:e.target.value})} /></FormGroup></Col>
            <Col md="4"><FormGroup><Label for="lead-last-name">Last name</Label><Input id="lead-last-name" maxLength="120" value={form.last_name} onChange={(e) => setForm({...form, last_name:e.target.value})} /></FormGroup></Col>
            <Col md="4"><FormGroup><Label for="lead-email">Email</Label><Input id="lead-email" type="email" value={form.email} onChange={(e) => setForm({...form, email:e.target.value})} /></FormGroup></Col>
            <Col md="4"><FormGroup><Label for="lead-phone">Phone</Label><Input id="lead-phone" value={form.phone} onChange={(e) => setForm({...form, phone:e.target.value})} /></FormGroup></Col>
            <Col md="4"><FormGroup><Label for="lead-source">Source</Label><Input id="lead-source" value={form.source} onChange={(e) => setForm({...form, source:e.target.value})} /></FormGroup></Col>
            <Col md="4"><FormGroup><Label for="lead-value">Estimated value</Label><Input id="lead-value" type="number" min="0" step="0.01" value={form.estimated_value} onChange={(e) => setForm({...form, estimated_value:e.target.value})} /></FormGroup></Col>
            <Col md="4"><FormGroup><Label for="lead-stage">Status</Label><Input id="lead-stage" type="select" value={form.status} onChange={(e) => setForm({...form, status:e.target.value})}>{["new","contacted","qualified","won","lost"].map((item)=><option key={item}>{item}</option>)}</Input></FormGroup></Col>
            <Col md="4"><FormGroup><Label for="lead-priority">Priority</Label><Input id="lead-priority" type="select" value={form.priority} onChange={(e) => setForm({...form, priority:e.target.value})}>{["low","normal","high"].map((item)=><option key={item}>{item}</option>)}</Input></FormGroup></Col>
            <Col xs="12"><FormGroup><Label for="lead-notes">Notes</Label><Input id="lead-notes" type="textarea" rows="3" maxLength="10000" value={form.notes} onChange={(e) => setForm({...form, notes:e.target.value})} /></FormGroup></Col>
            <Col xs="12"><Button color="primary" type="submit" disabled={saving}>{saving ? "Saving…" : editing ? "Update lead" : "Save lead"}</Button></Col>
          </Row></Form>}
          <Row className="mb-3">
            <Col md="8"><Label className="visually-hidden" for="lead-search">Search leads</Label><Input id="lead-search" placeholder="Search name or email" value={search} onChange={(e) => {setSearch(e.target.value);setPage(1);}} /></Col>
            <Col md="4"><Label className="visually-hidden" for="lead-status">Filter by status</Label><Input id="lead-status" type="select" value={status} onChange={(e) => {setStatus(e.target.value);setPage(1);}}><option value="">All statuses</option>{["new","contacted","qualified","won","lost"].map((item)=><option key={item}>{item}</option>)}</Input></Col>
          </Row>
          {loading ? <LoadingState label="Loading leads" /> :
          <div className="table-responsive"><Table hover className="align-middle"><thead><tr><th>Name</th><th>Contact</th><th>Source</th><th>Status</th><th>Value</th><th><span className="visually-hidden">Actions</span></th></tr></thead><tbody>
            {result.data.length === 0 && <tr><td colSpan="6" className="text-center text-muted py-5">No leads match this view.</td></tr>}
            {result.data.map((lead) => <tr key={lead.id}><td>{lead.first_name} {lead.last_name}</td><td>{lead.email || lead.phone || "—"}</td><td>{lead.source || "—"}</td><td><span className="badge badge-light-primary text-capitalize">{lead.status}</span></td><td>{lead.estimated_value ? Number(lead.estimated_value).toLocaleString(undefined,{style:"currency",currency:"USD"}) : "—"}</td><td className="text-end"><div className="d-flex gap-2 justify-content-end">{can("leads.update") && <Button color="link" className="p-0" onClick={() => edit(lead)}>Edit</Button>}{can("leads.delete") && <Button color="link" className="text-danger p-0" onClick={() => remove(lead)}>Delete</Button>}</div></td></tr>)}
          </tbody></Table></div>}
          <div className="d-flex justify-content-between align-items-center"><Button outline disabled={page <= 1} onClick={() => setPage(page-1)}>Previous</Button><span>Page {result.current_page} of {result.last_page}</span><Button outline disabled={page >= result.last_page} onClick={() => setPage(page+1)}>Next</Button></div>
        </CardBody>
      </Card>
    </Container>
  </>;
};

const ModulePage = ({ moduleKey }) => {
  const config = MODULES[moduleKey];
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const { user, dateRange } = useAuth();
  const load = useCallback(() => {
    setData(null);
    setError("");
    client.get("/dashboard", { params: { from: dateRange.start, to: dateRange.end } })
      .then(({ data: responseData }) => setData(responseData))
      .catch((requestError) => setError(getApiError(requestError)));
  }, [dateRange.start, dateRange.end, user?.company?.id]);
  useEffect(load, [load]);
  const chart = useMemo(() => {
    const statuses = data?.leads?.by_status || {};
    return { series: Object.values(statuses), options: { labels: Object.keys(statuses).map((x)=>x[0]?.toUpperCase()+x.slice(1)), chart:{type:"donut"}, legend:{position:"bottom"}, noData:{text:"No lead data yet"} } };
  }, [data]);
  const count = config.countKey && data ? data.modules[config.countKey] : null;

  const showChart = ["dashboard", "analytics", "business-impact"].includes(moduleKey);
  if (error) return <><Breadcrumbs mainTitle={config.title} parent="Workspace" title={config.title} /><Container fluid><WorkspaceContext /><ErrorState message={error} onRetry={load} /></Container></>;
  if (!data) return <><Breadcrumbs mainTitle={config.title} parent="Workspace" title={config.title} /><Container fluid><WorkspaceContext /><LoadingState label={`Loading ${config.title}`} /></Container></>;
  return <><Breadcrumbs mainTitle={config.title} parent="Workspace" title={config.title} /><Container fluid><WorkspaceContext /><Row>
    {showChart && <Col xl="6"><Card><CardHeader><h5>Lead pipeline</h5></CardHeader><CardBody>{chart.series.length ? <Chart options={chart.options} series={chart.series} type="donut" height={320} /> : <EmptyState title="No lead activity">Lead activity for this date range will appear here.</EmptyState>}</CardBody></Card></Col>}
    <Col xl={(moduleKey === "analytics" || moduleKey === "business-impact") ? "6" : "12"}><Card><CardHeader><h5>{config.title}</h5></CardHeader><CardBody><p>{config.description}</p>{count !== null && <h3>{count}</h3>}{moduleKey === "settings" && <dl className="row mb-0"><dt className="col-sm-3">Company</dt><dd className="col-sm-9">{user.company?.name || "—"}</dd><dt className="col-sm-3">Timezone</dt><dd className="col-sm-9">{user.company?.timezone || "—"}</dd></dl>}{count === 0 && <Alert color="light" className="mb-0">Nothing has been connected or recorded here yet.</Alert>}</CardBody></Card></Col>
  </Row></Container></>;
};

const SocialPresence = () => {
  const firstSegment = useLocation().pathname.split("/").filter(Boolean)[0] || "leads";
  return firstSegment === "leads" ? <Leads /> : <ModulePage moduleKey={firstSegment} />;
};

export default SocialPresence;
